import {
  BAND_LABELS,
  ALTERNATIVE_CONSTRAINT_ACTION,
  CATEGORIES,
  CTA_PRIMARY,
  DIMENSIONS,
  FRICTION_BAND_LABELS,
  FRICTION_CONTROL_ACTION,
  FRICTION_NARRATIVES,
  LIMITATIONS,
  MISMATCH_INTRO,
  MISMATCH_PAIRS,
  OTHER_POSSIBLE_CAUSES,
  PRIMARY_SIGNAL_NONE,
  STRONGEST_TIE,
} from "../data/review";

export const RESULT_EMAIL_ENDPOINT = "/api/operational-clarity-result-email.php";

const DIMENSION_MAP = Object.fromEntries(DIMENSIONS.map((dimension) => [dimension.key, dimension]));
const MISMATCH_MAP = Object.fromEntries(MISMATCH_PAIRS.map((mismatch) => [mismatch.area, mismatch]));
const CONTROLLED_ACTIONS = new Set([
  ...DIMENSIONS.map((dimension) => dimension.action),
  FRICTION_CONTROL_ACTION,
  ALTERNATIVE_CONSTRAINT_ACTION,
  ...[...DIMENSIONS.map((dimension) => dimension.name), "day-to-day friction"].map(
    (area) => `Check one recent example from ${area} to see why the answers point in different directions.`
  ),
]);

const dimensionResult = (profile, key) => {
  const result = profile.dimensions.find((dimension) => dimension.dimension === key);
  const config = DIMENSION_MAP[key];
  if (!result || !config || !BAND_LABELS[result.band]) {
    throw new Error(`Missing controlled result for ${key}`);
  }
  const narrative = result.band === "insufficient" ? "" : config.narratives[result.band];
  return narrative ? `${BAND_LABELS[result.band]} — ${narrative}` : BAND_LABELS[result.band];
};

const numberedText = (items) => items.map((item, index) => `${index + 1}. ${item}`).join("\n");

/**
 * Flatten an already-calculated profile into the live HostGator/Brevo
 * transactional-template contract. No answer map or written example is
 * accepted, read or copied into the result payload.
 */
export const buildResultEmailPayload = ({
  profile,
  email,
  website,
  startedAt,
  siteOrigin = typeof window !== "undefined" ? window.location.origin : "",
}) => {
  const category = CATEGORIES[profile.overallCategory];
  const eligible = profile.dimensions.filter((dimension) => dimension.band !== "insufficient");
  const questionAreas = [...eligible]
    .sort((a, b) => a.index - b.index)
    .slice(0, 2)
    .map((dimension) => dimension.dimension);
  const questions = questionAreas.map((key) => DIMENSION_MAP[key].question);
  if (profile.recommendedActions.some((action) => !CONTROLLED_ACTIONS.has(action))) {
    throw new Error("Unsupported result action");
  }
  const nextItems = [...questions, ...profile.recommendedActions];
  const mismatches = profile.mismatches.filter(
    (mismatch) => mismatch.level === "possible" || mismatch.level === "strong"
  );
  if (mismatches.some((mismatch) => !MISMATCH_MAP[mismatch.area])) {
    throw new Error("Unsupported result mismatch");
  }
  const frictionNarrative = [
    FRICTION_NARRATIVES[profile.friction.band],
    profile.friction.limitedEvidence ? "Based on limited recent evidence." : null,
  ].filter(Boolean).join(" ");
  const origin = siteOrigin.replace(/\/$/, "");

  if (!category || !FRICTION_BAND_LABELS[profile.friction.band] || !origin) {
    throw new Error("Incomplete controlled result-email content");
  }

  return {
    email: email.trim(),
    website: website || "",
    startedAt,
    areaReviewed: profile.areaAssessed,
    recurringConcern: profile.recurringConcern || "",
    overallHeading: category.label,
    overallNarrative: category.summary,
    frictionBand: FRICTION_BAND_LABELS[profile.friction.band],
    frictionNarrative,
    directionResult: dimensionResult(profile, "direction"),
    standardsResult: dimensionResult(profile, "standards"),
    ownershipResult: dimensionResult(profile, "ownership"),
    authorityResult: dimensionResult(profile, "authority"),
    behaviourResult: dimensionResult(profile, "behaviour"),
    worthCheckingTriggered: mismatches.length > 0,
    worthCheckingText: mismatches.length
      ? [MISMATCH_INTRO, ...mismatches.map((mismatch) => MISMATCH_MAP[mismatch.area].message)].join("\n\n")
      : "",
    whereToLookFirst: profile.primarySignal
      ? DIMENSION_MAP[profile.primarySignal].name
      : PRIMARY_SIGNAL_NONE,
    holdingUpBest: profile.strongestCondition
      ? DIMENSION_MAP[profile.strongestCondition].name
      : STRONGEST_TIE,
    whatToCheckNext: numberedText(nextItems),
    otherPossibleCauses: OTHER_POSSIBLE_CAUSES,
    whatThisDoesNotProve: LIMITATIONS.join("\n\n"),
    instrumentVersion: profile.instrumentVersion,
    consultationUrl: `${origin}${CTA_PRIMARY.to}`,
  };
};
